import { useCallback, useEffect, useRef, useState } from 'react'

import { PaymentMethodEnum, WalletPaymentMethod } from 'src/Constants/enums'
import { setPaymentMethod } from 'src/Redux/payment-process'

import { useAppDispatch } from './redux'

const WALLET_INITIALIZATION_TIMEOUT_MS = 5000
const DEFAULT_SELECTION_SETTLE_FRACTION = 10
const DEFAULT_SELECTION_SETTLE_MS =
  WALLET_INITIALIZATION_TIMEOUT_MS / DEFAULT_SELECTION_SETTLE_FRACTION

type UseWalletPaymentMethodsParams = {
  onChange?: (method: PaymentMethodEnum) => void
  isWalletExpected?: boolean
  canSelectWalletByDefault?: boolean
}

type UseWalletPaymentMethods = {
  walletPaymentMethods: WalletPaymentMethod[]
  isWalletInitializing: boolean
  onWalletAvailabilityChange: (methods: WalletPaymentMethod[]) => void
  onChangePaymentMethod: (method: PaymentMethodEnum) => void
}

const getPreferredMethod = (
  methods: WalletPaymentMethod[],
): WalletPaymentMethod =>
  methods.includes(PaymentMethodEnum.APPLE_PAY)
    ? PaymentMethodEnum.APPLE_PAY
    : methods[0]

export const useWalletPaymentMethods = ({
  onChange,
  isWalletExpected,
  canSelectWalletByDefault = true,
}: UseWalletPaymentMethodsParams = {}): UseWalletPaymentMethods => {
  const dispatch = useAppDispatch()
  const [walletPaymentMethods, setWalletPaymentMethods] = useState<
    WalletPaymentMethod[]
  >([])
  const [isWalletReady, setIsWalletReady] = useState(false)
  const hasManualSelectionRef = useRef(false)
  const hasAppliedDefaultRef = useRef(false)
  const latestMethodsRef = useRef<WalletPaymentMethod[]>([])
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const applyDefault = useCallback((): void => {
    if (hasManualSelectionRef.current || hasAppliedDefaultRef.current) {
      return
    }

    const methods = latestMethodsRef.current

    if (!methods.length) {
      return
    }

    hasAppliedDefaultRef.current = true
    dispatch(setPaymentMethod(getPreferredMethod(methods)))
  }, [dispatch])

  const onWalletAvailabilityChange = useCallback(
    (methods: WalletPaymentMethod[]): void => {
      setIsWalletReady(true)
      setWalletPaymentMethods((current) => {
        if (!methods.length) {
          return current.length ? [] : current
        }

        const addedToList = methods.filter(
          (method) => !current.includes(method),
        )

        return addedToList.length ? [...current, ...addedToList] : current
      })

      if (!methods.length) {
        return
      }

      const newlySeen = methods.filter(
        (method) => !latestMethodsRef.current.includes(method),
      )

      if (newlySeen.length) {
        latestMethodsRef.current = [...latestMethodsRef.current, ...newlySeen]
      }

      if (
        !canSelectWalletByDefault ||
        hasManualSelectionRef.current ||
        hasAppliedDefaultRef.current
      ) {
        return
      }

      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current)
      }

      settleTimeoutRef.current = setTimeout(
        applyDefault,
        DEFAULT_SELECTION_SETTLE_MS,
      )
    },
    [applyDefault, canSelectWalletByDefault],
  )

  const onChangePaymentMethod = useCallback(
    (method: PaymentMethodEnum): void => {
      hasManualSelectionRef.current = true

      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current)
      }

      onChange?.(method)
    },
    [onChange],
  )

  useEffect(() => {
    if (!isWalletExpected || isWalletReady) {
      return void 0
    }

    const timeoutId = setTimeout(
      () => setIsWalletReady(true),
      WALLET_INITIALIZATION_TIMEOUT_MS,
    )

    return () => clearTimeout(timeoutId)
  }, [isWalletExpected, isWalletReady])

  useEffect(
    () => (): void => {
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current)
      }
    },
    [],
  )

  return {
    isWalletInitializing: Boolean(isWalletExpected) && !isWalletReady,
    walletPaymentMethods,
    onWalletAvailabilityChange,
    onChangePaymentMethod,
  }
}
